import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Trash, MoreVertical } from "lucide-react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";

const IncomeList = () => {
  const [incomes, setIncomes] = useState([]);
  const [incomeHeads, setIncomeHeads] = useState([]);
  // const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [incomeToDelete, setIncomeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  // const [date, setDate] = useState("");

  const [formData, setFormData] = useState({
    income_head_id: "",
    name: "",
    invoice_number: "",
    amount: "",
    description: "",
    date: "",
    document: null,
  });

  const toast = useRef(null);
  const navigate = useNavigate();

  const breadcrumbItems = [
    { title: "Finance", link: "/" },
    { title: "Income List", link: "#" },
  ];

  const fetchIncomes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/incomes/list`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setIncomes(response.data.data || []);
    } catch (error) {
      console.error("Error fetching incomes:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch incomes",
        life: 3000,
      });
    }
    setLoading(false);
  };

  const fetchIncomeHeads = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/income-heads/list`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setIncomeHeads(response.data.data || []);
    } catch (error) {
      console.error("Error fetching income heads:", error);
    }
  };

  useEffect(() => {
    fetchIncomes();
    fetchIncomeHeads();
  }, []);

  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const toggle = () => {
    if (modal) {
      setSelectedIncome(null);
      setFormData({
        income_head_id: "",
        name: "",
        invoice_number: "",
        amount: "",
        description: "",
        date: "",
        document: null,
      });
    }
    setModal(!modal);
  };

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append("income_head_id", formData.income_head_id);
    data.append("name", formData.name);
    data.append("invoice_number", formData.invoice_number);
    data.append("amount", formData.amount);
    data.append("description", formData.description);
    data.append("date", formData.date);
    if (formData.document) {
      data.append("document", formData.document);
    }
    data.append("added_by", localStorage.getItem("user_id"));

    try {
      if (selectedIncome) {
        // Updated to use the correct edit endpoint
        await axios.post(`${API_BASE_URL}/invoice/update/${selectedIncome.id}`, data, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
        });
        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Income updated successfully",
          life: 3000,
        });
      } else {
        await axios.post(`${API_BASE_URL}/incomes/add`, data, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
        });
        toast.current.show({
          severity: "success",
          summary: "Added",
          detail: "Income added successfully",
          life: 3000,
        });
      }
      toggle();
      fetchIncomes();
    } catch (error) {
      console.error("Error saving income:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to save income",
        life: 3000,
      });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!incomeToDelete) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/incomes/delete/${incomeToDelete.id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Income deleted successfully",
        life: 3000,
      });
      fetchIncomes();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete income",
        life: 3000,
      });
    }
    setIncomeToDelete(null);
    setLoading(false);
    toggleDeleteModal();
  };

  const handleEdit = (income) => {
    setSelectedIncome(income);
    setFormData({
      income_head_id: income.income_head_id.toString(), // Ensure this is a string
      name: income.name,
      invoice_number: income.invoice_number,
      amount: income.amount,
      description: income.description,
      date: income.date,
      document: null, // Reset document to allow new upload
    });
    toggle();
  };

  const filteredIncomes = incomes.filter(income =>
    income.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    income.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const amountBodyTemplate = (rowData) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(rowData.amount);
  };

  const documentBodyTemplate = (rowData) => {
    if (!rowData.document) return "No Document";

    return (
      <a
        href={`${API_BASE_URL}/invoice/get-user-invoice/${rowData.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={async (e) => {
          // Optional: You can add a check here to verify the document exists
          try {
            await axios.head(`${API_BASE_URL}/invoice/get-user-invoice/${rowData.id}`, {
              headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
          } catch (error) {
            e.preventDefault();
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Document not found",
              life: 3000,
            });
          }
        }}
      >
        View Document
      </a>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Income List" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <span>Income List</span>
              <div className="d-flex align-items-center gap-3">
                <Input
                  type="text"
                  placeholder="Search by name or invoice"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: "300px" }}
                />
                <Button color="primary" onClick={toggle}>
                  <i className="mdi mdi-plus mr-1"></i> Add Income
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <DataTable
                value={filteredIncomes.slice(first, first + rows)}
                loading={loading}
                stripedRows
              >
                <Column header="SI No" body={(rowData, options) => first + options.rowIndex + 1} />
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column field="invoice_number" header="Invoice Number" sortable />
                <Column field="date" header="Date" sortable />
                <Column
                  field="income_head.name"
                  header="Income Head"
                  sortable
                  body={(rowData) => rowData.income_head?.name || "N/A"}
                />
                <Column field="amount" header="Amount" sortable body={amountBodyTemplate} />
                <Column
                  field="document"
                  header="Document"
                  body={documentBodyTemplate}
                />
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <Dropdown
                      isOpen={dropdownOpen === rowData.id}
                      toggle={() => toggleDropdown(rowData.id)}
                    >
                      <DropdownToggle tag="button" className="btn btn-light" disabled={loading}>
                        <MoreVertical size={20} />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => handleEdit(rowData)}>
                          <Edit size={16} className="me-2" />
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => {
                            setIncomeToDelete(rowData);
                            toggleDeleteModal();
                          }}
                          className="text-danger"
                        >
                          <Trash size={16} className="me-2" />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                />
              </DataTable>
              <Paginator
                first={first}
                rows={rows}
                totalRecords={filteredIncomes.length}
                onPageChange={onPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add/Edit Income Modal */}
      <Modal isOpen={modal} toggle={toggle} size="lg" centered>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggle}>
            {selectedIncome ? "Edit Income" : "Add Income"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Income Head</Label>
                  <div className="d-flex align-items-center gap-3">
                    <Input
                      type="select"
                      value={formData.income_head_id}
                      onChange={(e) =>
                        setFormData({ ...formData, income_head_id: e.target.value })
                      }
                      required
                    >
                      <option value="">Select Income Head</option>
                      {incomeHeads.map((head) => (
                        <option key={head.id} value={head.id.toString()}>
                          {head.name}
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="primary"
                      onClick={() => navigate("/admin/income-heads")}
                    >
                      Add New
                    </Button>
                  </div>
                </FormGroup>
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Invoice Number</Label>
                  <Input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) =>
                      setFormData({ ...formData, invoice_number: e.target.value })
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Date</Label>
                  <Flatpickr
                    className="form-control"
                    placeholder="Select Date"
                    options={{
                      dateFormat: "Y-m-d",
                      altInput: true,
                      altFormat: "F j, Y",
                      defaultDate: formData.date || new Date(),
                    }}
                    value={formData.date}
                    onChange={(date) => {
                      setFormData({ ...formData, date: date[0].toISOString().split('T')[0] });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup>
                  <Label>Attached Document</Label>
                  <Input
                    type="file"
                    onChange={(e) =>
                      setFormData({ ...formData, document: e.target.files[0] })
                    }
                  />
                  {selectedIncome?.document && (
                    <div className="mt-2">
                      <a
                        href={`${API_BASE_URL}/invoice/get-user-invoice/${selectedIncome.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Current Document
                      </a>
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {selectedIncome ? "Update" : "Add"}
              {loading && <Spinner className="ms-1" size="sm" />}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this income?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            Delete
            {loading && <Spinner className="ms-1" size="sm" />}
          </Button>
        </ModalFooter>
      </Modal>

      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default IncomeList;